
import { useState, useRef, useEffect, useCallback } from 'react';
//import Form from 'react-bootstrap/Form';
import CreatableSelect from 'react-select/creatable';
import * as types from '../shared/types';
import { formatSealWatermeterId } from '../app/WmUtils';

// The two possible shapes of `emptyArr.current` below - fully determined by props.name
// (fixed for a given field instance's whole lifetime, never changes across its renders) and
// never needing a new array identity per render. Module-level constants so the *same*
// reference is reused every time, rather than a fresh array literal being constructed and
// assigned on every render.
//
// GitLab #373 review (Critical #B, a real, live-reproduced unbounded render loop): building
// a brand-new array literal here on every [props] pass (this used to be frozen after mount,
// gated behind the now-removed isMountedComponent bug) fed into a render cascade -
// setCreatableValue's object always being a new reference -> the [creatableValue] effect's
// setLookupFieldReload(propState.name) call -> WmeterForm re-rendering all sibling
// LookUpFormField instances with fresh props -> this effect running again for each of them
// -> a new emptyArr.current again - that never let the cascade settle. Confirmed live: any
// interaction with a populated seal2 field (picking "üres", picking a real value, or
// clearing an existing real value) followed by blur spun forever (React 17 only warns
// "Maximum update depth exceeded" instead of throwing, so the screen just froze instead of
// crashing) - only "clearing an already-empty seal2", the one path the pre-existing test
// suite happened to cover, did not loop. Reusing one of these two stable references breaks
// that instability.
const SEAL2_EMPTY_OPTIONS = [{ label: "üres", value: "---" }];
const NON_SEAL2_EMPTY_OPTIONS = [];

/*eslint-disable eqeqeq*/
export const LookUpFormField = props => {
    // "---" is the shared "intentionally empty" sentinel value written to parentRef by the
    // clear path in onChange below. It has to be a real, non-empty string - not "" - because
    // other code (this file's own actualIDValue-derivation effect, and WmeterForm.jsx's
    // onInputChange) gates on `!!ref.current.value` to tell "a value is set" apart from
    // "nothing has been typed into this ref yet"; a true "" can't survive that gate (see
    // GitLab #222). seal2ID already used this sentinel (labeled "üres") before this fix to
    // represent "no seal fitted" as a real, selectable menu option; this reuses the exact
    // same value for wmID/seal1ID too, but only as something onChange can write when the
    // user clears the field - not as a selectable option in their menus (no "üres"/empty
    // semantics were asked for those two fields, only that they be clearable).
    const createOption = (label) => {
        if (label === '---') {
            return props.name === 'seal2ID' ? { label: 'üres', value: '---' } : { label: '', value: '---' };
        }
        return !!label ? { label: label, value: label } : { label: "", value: "" };
    };
    const optName = useRef();


    const [propState, setPropState] = useState(props);
    const [warnColor, setWarnColor] = useState(false);
    const [optListStat, setOptListStat] = useState();
    const [creatableValue, setCreatableValue] = useState(null);
    const [inputFieldValue, setInputFieldValue] = useState('');
    const [, updateState] = useState();
    const forceUpdate = useCallback(() => updateState({}), []);

    const emptyArr = useRef(SEAL2_EMPTY_OPTIONS);
    // Tracks whether the ref currently holds the "---" sentinel *because the user just
    // cleared it* (as opposed to never having had a value at all). Set true by onChange's
    // clear branch (and by picking seal2's "üres" option, which writes the same sentinel),
    // set false by any real pick/typed value. The defaultValue-resync effect below reads
    // this to avoid destroying the sentinel when WmeterForm.jsx's onInputChange commits the
    // clear (props.defaultValue transitions to null as a *result* of the clear, not as an
    // unrelated new value) - see that effect's comment for why this matters (GitLab #373
    // review).
    const justClearedRef = useRef(false);

    const findSameID = currentIDValue => (optName.current == types.PREFIX_WM
        ?
        propState.wmListOfTask.filter((_, idx) => (idx != propState.wmItem)).findIndex(wmii => {
            if (wmii.meter_identifier == currentIDValue) {
                return true;
            }

            return false;
        })
        :
        propState.wmListOfTask.findIndex((wmii, iii) => {
            if (iii !== propState.wmItem) {
                if ((wmii.plomba1_identifier == currentIDValue) || (wmii.plomba2_identifier == currentIDValue)) {
                    return true;
                }
            } else {
                if (propState.name == "seal1ID") {
                    if (wmii.plomba2_identifier == currentIDValue) {
                        return true;
                    }
                } else {
                    if (wmii.plomba1_identifier == currentIDValue) {
                        return true;
                    }
                }
            }

            return false;
        }));

    const setRedding = () => {
        if (!creatableValue)
            return;
        const currentIDValue = creatableValue?.value;
        // "---" is the shared "intentionally empty" sentinel (see createOption above) -
        // whether it got there via seal2's "üres" menu option or via clearing wmID/seal1ID/
        // seal2ID, there is nothing to flag as a duplicate.
        if (currentIDValue == '---') {
            if (!!warnColor) {
                setWarnColor(false);
            }
            return;
        }

        if (!!currentIDValue) {
            let isRedding = false;
            if (props?.optDataList.current?.length > 0) {
                let currentSelection = propState.optDataList.current.find(x => x[optName.current] == currentIDValue);
                if (!!currentSelection) {
                    const foundSameID = findSameID(currentIDValue);

                    if (!!(foundSameID > -1)) {
                        isRedding = true;
                    }
                } else {

                    isRedding = true;
                }
            } else {

                isRedding = true;
            }
            setWarnColor(isRedding);

            forceUpdate();

        }
    }

    useEffect(() => {
        setPropState(props);
        if (props?.optDataList.current?.length > 0) {
            setOptListStat([...emptyArr.current,
            ...props?.optDataList.current?.filter(oii => (oii.duty === null))
                .map(oii => createOption(oii[optName.current]?.toString()))]);
        } else {
            if (optListStat != emptyArr.current)
                setOptListStat(emptyArr.current);
        }

        // Reuse the same stable reference every time (see the module-level constants above)
        // instead of constructing a new array literal on every pass - see Critical #B.
        emptyArr.current = props.name === 'seal2ID' ? SEAL2_EMPTY_OPTIONS : NON_SEAL2_EMPTY_OPTIONS;
        optName.current = props?.optDataList.current?.[0]?.hasOwnProperty(types.PREFIX_WM) ? types.PREFIX_WM : types.PREFIX_SEAL;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props]);

    // Resync the field's bookkeeping ref value (and, redundantly but harmlessly, its
    // displayed value) from props.defaultValue whenever the parent genuinely pushes a new
    // one - e.g. a barcode scan updating the model directly (WmeterForm.jsx's
    // barcodeScannerOn effect sets idRef.current.value itself too, but a later unrelated
    // re-render should not un-set it). Keyed on the primitive props.defaultValue itself -
    // not the whole `props` object, whose identity changes on every parent render - so this
    // only fires on a genuine value change, never on an unrelated re-render.
    //
    // IMPORTANT (corrected during GitLab #373 review - the original version of this comment
    // was wrong): props.defaultValue does NOT stay in sync with the ref through a clear.
    // WmeterForm.jsx's onInputChange commits a clear by writing meter_identifier = **null**
    // (not "---") into the wmlist item, so once that blur re-renders this component,
    // props.defaultValue transitions from the old real value straight to null/undefined -
    // a genuine change by this effect's own [props.defaultValue] dependency, which WOULD
    // fire and overwrite parentRef.current.value from "---" to null if left unguarded. That
    // destroys the sentinel that WmeterForm.jsx's meterIdCleared/seal1IdCleared checks
    // depend on staying readable as "---" on every later, unrelated blur (see the
    // "recomputed fresh from the still-'---' ref every time" comments there) - without it,
    // re-selecting "Ép" on a later blur is no longer recognized as "still cleared", and the
    // DB CheckConstraint #222 protects against (an identifier-less meter/seal marked "Ép")
    // can be silently reintroduced.
    //
    // justClearedRef (declared above, set by onChange) is how this effect tells "defaultValue
    // just became empty because the field's own clear was committed" apart from "the parent
    // pushed a genuinely different, real value" (e.g. a barcode scan, which sets a truthy
    // defaultValue and thus falls through to the normal resync below, also clearing this
    // flag). Only the former must be skipped - skipping unconditionally whenever
    // justClearedRef is true would wrongly freeze the field forever once cleared.
    //
    // Separately fixes a real, previously-latent bug (found while addressing GitLab #373):
    // the old "isMountedComponent" ref was meant to gate this whole resync to "only while
    // still mounted", but useEffect calls its cleanup on every dependency change, not only
    // on true unmount - so isMountedComponent.current flipped to false permanently after the
    // very first commit, and this resync never ran again for the rest of the component's
    // life (e.g. a barcode scan's new value was never picked up here, only via other means).
    useEffect(() => {
        if (justClearedRef.current && !props.defaultValue) {
            // The parent's data model catching up to reflect our own clear - not a new
            // value to resync to. Leave the "---" sentinel (and the visible "cleared" state
            // it already produced) exactly as onChange's clear branch left it.
            return;
        }
        justClearedRef.current = false;
        setCreatableValue(createOption(props.defaultValue));
        if (props?.parentRef?.current) {
            props.parentRef.current.value = props.defaultValue;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.defaultValue]);

    useEffect(() => {
        if (propState?.optDataList.current?.length > 0) {
            setOptListStat([...emptyArr.current,
            ...propState?.optDataList.current?.filter(oii => (oii.duty === null))
                .map(oii => createOption(oii[optName.current]?.toString()))]);
        } else {

            if (optListStat != emptyArr.current)
                setOptListStat(emptyArr.current);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propState.optDataList]);

    useEffect(() => {
        setRedding();

        propState.setLookupFieldReload(propState.name)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creatableValue]);

    useEffect(() => {
        let actualIDValue = undefined;
        if (!!(propState?.parentRef?.current?.value?.length > 0)) {
            actualIDValue = propState.parentRef.current.value;
        } else if (!!(propState.defaultValue?.length > 0)) {
            actualIDValue = propState.defaultValue;
        }

        if (!!(propState?.optDataList.current?.length > 0)) {
            if (!!actualIDValue && !!creatableValue  &&(creatableValue.value !==null) && (actualIDValue != creatableValue?.value)) {
                let prevSelection = propState.optDataList.current.find(x => x[optName.current] == creatableValue.value);
                if (!!prevSelection) {
                    const foundSameID = findSameID(creatableValue.value);
                    if (!(foundSameID > -1)) {
                        if (prevSelection.duty !== null) {
                            prevSelection.duty = null;

                            if (props?.optDataList.current?.length > 0)
                                setOptListStat([...emptyArr.current,
                                ...propState?.optDataList.current?.filter(oii => (oii.duty === null))
                                    .map(oii => createOption(oii[optName.current]?.toString()))]);
                        }
                    }
                }
            }

            if (!!actualIDValue && actualIDValue.length > 0) {
                let currSelection = propState.optDataList.current.find(x => x[optName.current] == actualIDValue);
                if (!!currSelection) {
                    const isSameDuty = !!currSelection.duty && currSelection.duty.taskId === propState.taskId && currSelection.duty.index === propState.wmItem;
                    if (!isSameDuty) {
                        /// duty is task-relative (an index into propState.taskId's own wmlist),
                        /// so it must be tagged with that task id - otherwise this selection
                        /// gets misresolved if a different task is uploaded later while this
                        /// one is still open in the background.
                        currSelection.duty = { taskId: propState.taskId, index: propState.wmItem };

                        if (props?.optDataList.current?.length > 0)
                            setOptListStat([...emptyArr.current,
                            ...propState?.optDataList.current?.filter(oii => (oii.duty === null))
                                .map(oii => createOption(oii[optName.current]?.toString()))]);
                    }
                }

                setCreatableValue(createOption(actualIDValue));
            }
        } else {
            if (optListStat != emptyArr.current)
                setOptListStat(emptyArr.current);
            if (!!actualIDValue && actualIDValue.length > 0) {
                setCreatableValue(createOption(actualIDValue));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [optListStat, propState?.parentRef?.current?.value, propState.defaultValue]);

    useEffect(() => {
        if (!!propState.lookupFieldReload && (propState.lookupFieldReload != propState.name)) {
            setRedding();

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propState.lookupFieldReload]);

    // Refers to on change event of the select field of the component.
    const onChange = (newOpt, action) => {
        if (!newOpt) {
            // react-select calls onChange(null, action) when the value is cleared - the
            // "x" clear indicator, Backspace on an empty search box removing a single
            // value, or Escape (isClearable enables all three for a non-multi select, and
            // all three go through this same call). Previously this crashed on
            // newOpt.value, which left parentRef.current.value at its last selection - so
            // the field appeared to revert to the last-known-good value instead of
            // clearing. Write the shared "---" sentinel instead of a true "" (see
            // createOption above for why a true "" can't be used here).
            //
            // creatableValue is *not* updated eagerly elsewhere in this file for a normal
            // pick - the field only visually catches up once WmeterForm re-renders after
            // blur (an existing, pre-#222 lag this fix does not need to touch, since normal
            // picks aren't what #222 reported). Clearing must update immediately though -
            // that visible "nothing changed" is exactly the reported symptom - so this
            // branch calls setCreatableValue itself. That eager update means the old value
            // is no longer observable from the later actualIDValue-derivation effect (which
            // infers a value change by comparing the ref's new value against the -by-then-
            // already-updated- creatableValue), which is exactly why that effect wasn't
            // releasing the previous selection's warehouse "duty" tag on a clear. So do the
            // release explicitly here instead, using the *current* (about-to-be-replaced)
            // creatableValue.value as "old" and "---" as "new" - both in hand at the same
            // instant, no lagging/inferring needed.
            const oldValue = creatableValue?.value;
            const newValue = '---';

            if (propState?.parentRef?.current) {
                propState.parentRef.current.value = newValue;
            }
            // Marks the sentinel as "just written by an explicit clear" - see the
            // defaultValue-resync effect above for why this must survive the parent's own
            // re-render once WmeterForm.jsx's onInputChange commits this clear.
            justClearedRef.current = true;

            if (!!oldValue && oldValue !== newValue && propState?.optDataList?.current?.length > 0) {
                const prevSelection = propState.optDataList.current.find(x => x[optName.current] == oldValue);
                if (!!prevSelection && prevSelection.duty !== null) {
                    const foundSameID = findSameID(oldValue);
                    if (!(foundSameID > -1)) {
                        prevSelection.duty = null;
                        setOptListStat([...emptyArr.current,
                        ...propState.optDataList.current.filter(oii => (oii.duty === null))
                            .map(oii => createOption(oii[optName.current]?.toString()))]);
                    }
                }
            }

            setCreatableValue(createOption(newValue));
            return;
        }

        if (propState?.parentRef?.current) {
            propState.parentRef.current.value = newOpt.value;
        }
        // seal2ID's "üres" menu option is a normal (truthy newOpt) pick that also writes the
        // "---" sentinel - it means the same "explicitly emptied" thing as the clear branch
        // above, so it must set the flag too. Any other, real pick clears it.
        justClearedRef.current = (newOpt.value === '---');
    }

    // Refers to on input change event of the text field of the component.
    const handleInput = (e) => {
        // Apply regex to only allow numbers and no prefix zeros.
        setInputFieldValue(formatSealWatermeterId(e));
    }

    const onCreateNew = (newVal) => {
        if (propState?.parentRef?.current) {
            propState.parentRef.current.value = newVal;
        }
        // A freshly typed/created value is never a clear.
        justClearedRef.current = false;
    }

    const colourWarn = theme => {
        const retVal = {
            ...theme,
            colors: { ...theme.colors }
        };
        retVal.colors.neutral0 = warnColor ? '#ff8080' : 'white';
        return retVal;
    }

    return (
        <>
            {!!creatableValue && <CreatableSelect
                // Spread propState first so none of our explicit, load-bearing props below
                // (isClearable in particular - see comment on it) can ever be silently
                // overridden by a future caller passing a same-named prop through props.
                {...(({ parentRef, optDataList, wmItem, wmListOfTask, taskId, lookupFieldReload, setLookupFieldReload, disabled, ...obj }) => obj)(propState)}
                onCreateOption={onCreateNew}
                value={creatableValue}
                onChange={onChange}
                onInputChange={handleInput}
                inputValue={inputFieldValue}
                options={optListStat}
                // react-select only renders the "x" clear control (and honors Backspace
                // as a clear-to-empty on a single, non-multi value) when isClearable is
                // true. It is not part of react-select's own defaults (isMulti/isClearable
                // both default to false), and nothing else in this component enabled it,
                // so without this the field had no way to reach onChange(null, ...) at
                // all - the clear affordance simply didn't exist. See GitLab #222.
                isClearable
                ref={propState.parentRef}
                theme={theme => (colourWarn(theme))}
                formatCreateLabel={txt => (`Új azonosító: "${txt}"`)}
                noOptionsMessage={() => "Nincs adat"}
                isDisabled={props.disabled}
            />}

        </>
    );
}
